import React, { useState, useEffect, useMemo } from 'react'
import { Reply, TrendingUp, User, MessageSquare, Eye, ExternalLink, ArrowLeft, Download, Filter, X, Edit2, Save, ChevronLeft, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts'
import { utcStartOfDay, utcEndOfDay, utcStartOfMonth, utcStartOfPrevMonth, utcEndOfPrevMonth } from '@/lib/utc-date-utils'
import { useConversionFunnel } from '@/hooks/useConversionFunnel'
import { supabase } from '@/integrations/supabase/client'
import { resolveClient } from '@/lib/resolve-client-filter'
import { toast } from "@/hooks/use-toast"
import { BaseDetailView } from './BaseDetailView'
import { TimeframeSelector } from './TimeframeSelector'
import { ReplyDetailModal } from './ReplyDetailModal'
import { DataTableColumnConfig, ColumnConfig } from './DataTableColumnConfig'
import Papa from 'papaparse'
import { useQuery, useQueryClient } from '@tanstack/react-query'

interface TotalRepliesDetailViewProps {
  onBack: () => void
  clientFilter?: string
  timeFrame?: string
  customDateRange?: {
    from: Date
    to: Date
  }
  onTimeFrameChange?: (timeFrame: string) => void
  onCustomDateRangeChange?: (dateRange: { from: Date; to: Date } | undefined) => void
  initialCategoryFilter?: string // Add initial category filter prop
}

export const TotalRepliesDetailView = ({ 
  onBack, 
  clientFilter = 'all', 
  timeFrame = 'MTD', 
  customDateRange,
  onTimeFrameChange,
  onCustomDateRangeChange,
  initialCategoryFilter = 'all' // Default to 'all' if not specified
}: TotalRepliesDetailViewProps) => {
  const [selectedRecord, setSelectedRecord] = useState<any>(null)
  const [selectedReply, setSelectedReply] = useState<any>(null)
  
  // State for pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  
  // State for reply detail modal
  const [isModalOpen, setIsModalOpen] = useState(false)
  
  // Editing state for category
  const [editingCategory, setEditingCategory] = useState(false)
  const [editedCategory, setEditedCategory] = useState('')
  const [savingCategory, setSavingCategory] = useState(false)
  
  // Filter states
  const [categoryFilter, setCategoryFilter] = useState<string>(initialCategoryFilter)
  const [clientIdFilter, setClientIdFilter] = useState<string>('')
  const [fromEmailFilter, setFromEmailFilter] = useState<string>('')
  const [toEmailFilter, setToEmailFilter] = useState<string>('')
  const [campaignFilter, setCampaignFilter] = useState<string>('')
  const [showFilters, setShowFilters] = useState(false)

  // Available category options for dropdown
  const categoryOptions = [
    'Interested',
    'Information Request', 
    'Meeting Request',
    'Meeting Booked',
    'Specific Questions',
    'Out of Office',
    'Auto-Reply',
    'Automated Response',
    'No Longer Works Here',
    'Customer Service',
    'Not Interested',
    'Unsubscribe Request',
    'Spam/Junk',
    'Complaint',
    'Negative',
    'Rude',
    'Hostile',
    'Do Not Contact',
    'Neutral',
    'Wrong Person',
    'Reach Out Later',
    'OOO',
    'Vacation',
    'Holiday',
    'Maternity',
    'Paternity',
    'Leave',
    'Traveling',
    'Away',
    'Unavailable'
  ]
  
  // Column configuration state
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>([
    { id: 'lead', label: 'Lead', visible: true, order: 0 },
    { id: 'sender', label: 'Sender', visible: true, order: 1 },
    { id: 'campaign', label: 'Campaign', visible: true, order: 2 },
    { id: 'category', label: 'Category', visible: true, order: 3 },
    { id: 'reply_count', label: 'Reply Count', visible: true, order: 4 },
    { id: 'reply', label: 'Reply', visible: true, order: 5 },
    { id: 'actions', label: 'Actions', visible: true, order: 6 },
    { id: 'time_replied', label: 'Time Replied', visible: false, order: 7 },
    { id: 'from_email', label: 'From Email', visible: false, order: 8 },
    { id: 'to_email', label: 'To Email', visible: false, order: 9 },
    { id: 'to_name', label: 'Contact', visible: false, order: 10 },
    { id: 'campaign_name', label: 'Campaign Name', visible: false, order: 11 },
    { id: 'campaign_id', label: 'Campaign ID', visible: false, order: 12 },
    { id: 'response_category', label: 'Response Category', visible: false, order: 13 },
    { id: 'reply_type', label: 'Reply Type', visible: false, order: 14 },
    { id: 'client_id', label: 'Client ID', visible: false, order: 15 },
    { id: 'reply_body', label: 'Reply Content', visible: false, order: 16 },
    { id: 'subject', label: 'Subject', visible: false, order: 17 },
    { id: 'sent_message', label: 'Original Message', visible: false, order: 18 },
    { id: 'app_url', label: 'Smartlead URL', visible: false, order: 19 },
  ])
  
  const queryClient = useQueryClient()
  const { data: conversionData } = useConversionFunnel({ 
    timeFrame,
    clientId: clientFilter,
    dateRange: customDateRange
  })

  // State for positive replies count from funnel (authoritative source)
  const [positiveRepliesFromFunnel, setPositiveRepliesFromFunnel] = useState<number>(0)

  // Update positive replies count when conversion data changes
  useEffect(() => {
    if (conversionData?.positiveReplies !== undefined) {
      setPositiveRepliesFromFunnel(conversionData.positiveReplies)
    }
  }, [conversionData])

  // Get the same date range that conversion funnel uses
  // All boundaries use UTC to match Postgres timestamps regardless of browser timezone.
  const getDateRange = () => {
    const now = new Date()

    if (customDateRange?.from && customDateRange?.to) {
      return {
        startDate: utcStartOfDay(customDateRange.from),
        endDate: utcEndOfDay(customDateRange.to)
      }
    }

    let startDate = new Date()
    let endDate = now

    switch (timeFrame) {
      case 'today':
        startDate = utcStartOfDay(now)
        endDate = utcEndOfDay(now)
        break
      case 'LAST_MONTH':
        startDate = utcStartOfPrevMonth(now)
        endDate = utcEndOfPrevMonth(now)
        break
      case 'MTD':
        startDate = utcStartOfMonth(now)
        endDate = now
        break
      case 'last7days':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
        break
      case 'last30days':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
        break
      case 'last90days':
        startDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000)
        break
      default:
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    }

    return { startDate, endDate }
  }

  // Cached query for chart data (complete breakdown without limit)
  const { data: chartData = [] } = useQuery({
    queryKey: ['totalRepliesChart', clientFilter, timeFrame, customDateRange],
    queryFn: async () => {
      const { startDate: correctStartDate, endDate: correctEndDate } = getDateRange()
      const { workspaceName } = await resolveClient(clientFilter)

      let query = supabase
        .from('masterinbox_replies')
        .select(`
          label_name,
          reply_category_type
        `)
        .eq('reply_count', 1)

      // Client filtering
      if (workspaceName) {
        query = query.eq('workspace_name', workspaceName)
      }

      // Date filtering
      query = query
        .gte('created_at', correctStartDate.toISOString())
        .lte('created_at', correctEndDate.toISOString())

      const { data, error } = await query

      if (error) {
        console.error('Error fetching chart data:', error)
        return []
      }

      // Process data for charts using proper database categorization
      return (data || []).map(reply => {
        const computedType = reply.reply_category_type || categorizeReply(reply.label_name)
        return {
          ...reply,
          reply_type_computed: computedType
        }
      })
    },
    staleTime: 10 * 60 * 1000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    retry: 1,
    gcTime: 15 * 60 * 1000
  })

  // Cached query for total replies data (limited for table performance)
  const { data: totalRepliesData = [], isLoading, isFetching } = useQuery({
    queryKey: ['totalReplies', clientFilter, timeFrame, customDateRange],
    queryFn: async () => {
      const { startDate: correctStartDate, endDate: correctEndDate } = getDateRange()
      const { workspaceName } = await resolveClient(clientFilter)

      console.log('Fetching total replies:', {
        start: correctStartDate.toISOString(),
        end: correctEndDate.toISOString(),
        client: clientFilter,
        timeFrame
      })

      const selectFields = `
          id,
          created_at,
          to_address,
          email,
          to_name,
          campaign_name,
          campaign_id,
          label_name,
          reply_category_type,
          workspace_name,
          reply_count,
          text,
          reply_message,
          subject
        `

      let query = supabase
        .from('masterinbox_replies')
        .select(selectFields)
        .eq('reply_count', 1)

      // Client filtering
      if (workspaceName) {
        query = query.eq('workspace_name', workspaceName)
      }

      query = query
        .gte('created_at', correctStartDate.toISOString())
        .lte('created_at', correctEndDate.toISOString())
        .order('created_at', { ascending: false })
        .limit(100)

      const { data, error } = await query

      if (error) {
        console.error('Error fetching raw replies:', error)
        throw error
      }

      console.log('Raw replies fetched:', data?.length)

      // Process data to use proper database categorization
      const processedData = (data || []).map(reply => {
        const computedType = reply.reply_category_type || categorizeReply(reply.label_name)
        
        return {
          ...reply,
          reply_type_computed: computedType
        }
      })
      
      return processedData
    },
    staleTime: 10 * 60 * 1000, // Data is fresh for 10 minutes (increased from 2)
    refetchInterval: false, // Disable auto-refetch to improve performance
    refetchOnWindowFocus: false, // Disable refetch on focus to improve performance
    retry: 1, // Reduce retries to 1
    gcTime: 15 * 60 * 1000 // Keep in cache for 15 minutes
  })

  // Helper function for manual categorization (fallback from label_name)
  const categorizeReply = (label: string | null | undefined): string => {
    if (!label) return 'ai_messup';

    const cat = label.toLowerCase().trim();

    // Positive responses
    if (['pricing inquiry', 'specific questions', 'meeting request', 'meeting booked', 'interested', 'information request'].includes(cat)) {
      return 'positive';
    }

    // OOO / Bounced responses
    if (['out of office', 'auto-reply', 'sender originated bounce', 'no longer works here', 'reach out later'].includes(cat)) {
      return 'ooo_bounced';
    }

    // Negative responses
    if (['do not contact', 'not interested', 'campaign mess up'].includes(cat)) {
      return 'negative';
    }

    // AI messup
    if (['wrong person'].includes(cat)) {
      return 'ai_messup';
    }

    return 'ai_messup';
  };

  const formatNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M'
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k'
    return num.toString()
  }

  // Filter the raw replies based on current filters
  const filteredReplies = useMemo(() => {
    return totalRepliesData.filter(reply => {
      // Category filter using reply_type_computed
      if (categoryFilter && categoryFilter !== "all") {
        const replyType = reply.reply_type_computed || 'ai_messup';

        if (categoryFilter === 'ooo_bounced') {
          if (replyType !== 'ooo_bounced') return false;
        } else {
          if (replyType !== categoryFilter) return false;
        }
      }
      if (clientIdFilter && clientIdFilter !== "all" && reply.workspace_name?.toString() !== clientIdFilter) return false
      if (fromEmailFilter && !reply.to_address?.toLowerCase().includes(fromEmailFilter.toLowerCase())) return false
      if (toEmailFilter && !reply.email?.toLowerCase().includes(toEmailFilter.toLowerCase())) return false
      if (campaignFilter && !reply.campaign_name?.toLowerCase().includes(campaignFilter.toLowerCase())) return false
      return true
    })
  }, [totalRepliesData, categoryFilter, clientIdFilter, fromEmailFilter, toEmailFilter, campaignFilter])
  
  // Pagination logic
  const totalPages = Math.ceil(filteredReplies.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const paginatedReplies = filteredReplies.slice(startIndex, endIndex)
  
  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1)
  }, [categoryFilter, clientIdFilter, fromEmailFilter, toEmailFilter, campaignFilter])
  
  // Handlers
  const handleRowClick = (reply: any) => {
    setSelectedReply(reply)
    setIsModalOpen(true)
  }
  
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedReply(null)
  }
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page)
  }

  // Get unique values for filter dropdowns
  const uniqueCategories = useMemo(() => {
    const categories = [...new Set(totalRepliesData.map(r => r.label_name).filter(Boolean))]
    return categories.sort()
  }, [totalRepliesData])

  const uniqueWorkspaces = useMemo(() => {
    const workspaces = [...new Set(totalRepliesData.map(r => r.workspace_name).filter(Boolean))]
    return workspaces.sort()
  }, [totalRepliesData])

  // Calculate average per day based on timeframe
  const calculateAvgPerDay = () => {
    const { startDate, endDate } = getDateRange()
    const totalDays = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24))
    return totalDays > 0 ? Math.round(totalRepliesData.length / totalDays) : 0
  }

  // Get category breakdown for charts - using label_name column
  const getCategoryBreakdown = useMemo(() => {
    // Use filteredReplies instead of chartData to respect the category filter
    const categoryCounts = filteredReplies.reduce((acc, reply) => {
      const category = reply.label_name || 'Unknown'
      acc[category] = (acc[category] || 0) + 1
      return acc
    }, {} as Record<string, number>)

    const totalReplies = filteredReplies?.length || 0
    return Object.entries(categoryCounts).map(([category, count]) => ({
      category,
      count,
      percentage: totalReplies > 0 ? ((Number(count) / Number(totalReplies)) * 100).toFixed(1) : '0'
    }))
  }, [filteredReplies])

  // Get simplified category breakdown for positive/negative/ooo_bounced/ai_messup
  // Use reply_category_type column from database
  const getSimplifiedCategoryBreakdown = useMemo(() => {
    const counts = {
      positive: 0,
      negative: 0,
      'ooo_bounced': 0,
      'ai_messup': 0
    }

    chartData.forEach(reply => {
      const replyType = reply.reply_type_computed || 'ai_messup';

      if (counts.hasOwnProperty(replyType)) {
        counts[replyType]++;
      }
    })

    return [
      { name: 'Positive', value: counts.positive, fill: '#22c55e' },
      { name: 'Negative', value: counts.negative, fill: '#ef4444' },
      { name: 'OOO/Bounced', value: counts['ooo_bounced'], fill: '#8b5cf6' },
      { name: 'AI Messup', value: counts['ai_messup'], fill: '#6b7280' }
    ]
  }, [chartData])

  // Helper function to format email content like a normal email
  const formatEmailContent = (html: string | null | undefined): string => {
    if (!html) return 'N/A';
    
    // Remove HTML tags but preserve some structure
    let text = html
      .replace(/<br\s*\/?>/gi, '\n')  // Convert <br> to newlines
      .replace(/<\/p>/gi, '\n\n')     // Convert </p> to double newlines
      .replace(/<\/div>/gi, '\n')     // Convert </div> to newlines
      .replace(/<[^>]*>/g, ' ')       // Remove all other HTML tags
      .replace(/&nbsp;/gi, ' ')       // Replace &nbsp; with spaces
      .replace(/&amp;/gi, '&')        // Replace &amp; with &
      .replace(/&lt;/gi, '<')         // Replace &lt; with <
      .replace(/&gt;/gi, '>')         // Replace &gt; with >
      .replace(/&quot;/gi, '"');      // Replace &quot; with "
    
    // Clean up excessive whitespace but preserve paragraph breaks
    text = text
      .replace(/[ \t]+/g, ' ')        // Multiple spaces/tabs to single space
      .replace(/\n[ \t]+/g, '\n')     // Remove spaces at start of lines
      .replace(/[ \t]+\n/g, '\n')     // Remove spaces at end of lines
      .replace(/\n{3,}/g, '\n\n')     // Multiple newlines to double newlines
      .trim();
    
    return text || 'N/A';
  };

  // Enhanced email content renderer for table (shortened)
  const formatEmailContentShort = (html: string | null | undefined): string => {
    const fullContent = formatEmailContent(html);
    if (fullContent.length > 200) {
      return fullContent.substring(0, 200) + '...';
    }
    return fullContent;
  };

  // Column configuration handlers
  const handleColumnToggle = (columnId: string) => {
    setColumnConfigs(prev => 
      prev.map(col => 
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    )
  }

  const handleColumnReorder = (dragIndex: number, hoverIndex: number) => {
    setColumnConfigs(prev => {
      const newConfigs = [...prev].sort((a, b) => a.order - b.order)
      const draggedItem = newConfigs[dragIndex]
      newConfigs.splice(dragIndex, 1)
      newConfigs.splice(hoverIndex, 0, draggedItem)
      
      return newConfigs.map((col, index) => ({ ...col, order: index }))
    })
  }

  // Helper function for cell styling
  const getCellClassName = (columnId: string) => {
    switch (columnId) {
      case 'time_replied':
      case 'from_email':
      case 'to_email':
      case 'client_id':
      case 'reply_count':
        return 'font-mono text-xs'
      case 'to_name':
        return 'text-xs'
      case 'campaign_name':
        return 'text-xs max-w-32 truncate'
      case 'reply_body':
        return 'text-xs max-w-48 truncate'
      case 'subject':
      case 'sent_message':
        return 'text-xs max-w-64 truncate'
      case 'app_url':
        return 'text-xs'
      default:
        return 'text-xs'
    }
  }

  // Helper function for rendering cell content
  const renderCellContent = (reply: any, columnId: string) => {
    switch (columnId) {
      case 'time_replied':
        return reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A'
      case 'from_email':
        return reply.to_address || 'N/A'
      case 'to_email':
        return reply.email || 'N/A'
      case 'to_name':
        return reply.to_name || 'Unknown'
      case 'campaign_name':
        return (
          <div title={reply.campaign_name}>
            {reply.campaign_name || 'N/A'}
          </div>
        )
      case 'category':
        return (
          <Badge variant="outline" className={`text-xs ${
            reply.reply_category_type === 'positive' ? 'text-green-600 border-green-200' :
            reply.reply_category_type === 'negative' ? 'text-red-600 border-red-200' :
            'text-gray-600 border-gray-200'
          }`}>
            {reply.label_name || 'N/A'}
          </Badge>
        )
      case 'client_id':
        return reply.workspace_name || 'N/A'
      case 'reply_count':
        return reply.reply_count
      case 'reply_body':
        return (
          <div title={reply.text}>
            {reply.text || 'N/A'}
          </div>
        )
      case 'subject':
        return (
          <div title={reply.subject}>
            {reply.subject || 'N/A'}
          </div>
        )
      case 'sent_message':
        return (
          <div title={reply.sent_message}>
            {reply.sent_message || 'N/A'}
          </div>
        )
      case 'app_url':
        return reply.app_url ? (
          <a 
            href={reply.app_url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 underline"
          >
            View
          </a>
        ) : 'N/A'
      case 'actions':
        return (
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" className="h-8 w-8 p-0">
                <Eye className="h-4 w-4" />
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="text-lg font-semibold">Reply Details</DialogTitle>
              </DialogHeader>
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Sender</label>
                    <p className="font-mono text-base mt-1 p-2 bg-muted/50 rounded">{reply.to_address || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Lead Email</label>
                    <p className="font-mono text-base mt-1 p-2 bg-muted/50 rounded">{reply.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Contact Name</label>
                    <p className="text-base mt-1 p-2 bg-muted/50 rounded">{reply.to_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Campaign</label>
                    <p className="text-base mt-1 p-2 bg-muted/50 rounded">{reply.campaign_name || 'Unknown'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Subject</label>
                    <p className="text-base mt-1 p-2 bg-muted/50 rounded">{reply.subject || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Reply Time</label>
                    <p className="font-mono text-base mt-1 p-2 bg-muted/50 rounded">{reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Category</label>
                    <p className="text-base mt-1 p-2 bg-muted/50 rounded">{reply.label_name || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Reply Content</label>
                  <div className="mt-2 p-4 bg-muted/50 rounded-md max-h-48 overflow-y-auto border">
                    <p className="whitespace-pre-wrap text-base leading-relaxed">{reply.text || reply.reply_message || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )
      default:
        return reply[columnId] || 'N/A'
    }
  }

  // Clear all filters
  const clearAllFilters = () => {
    setCategoryFilter('all')
    setClientIdFilter('all')
    setFromEmailFilter('')
    setToEmailFilter('')
    setCampaignFilter('')
  }

  // Export to CSV
  const exportToCSV = () => {
    const csvData = filteredReplies.map(reply => ({
      'Time Replied': reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A',
      'Sender Email': reply.to_address || 'N/A',
      'Lead Email': reply.email || 'N/A',
      'Contact Name': reply.to_name || 'Unknown',
      'Campaign Name': reply.campaign_name || 'N/A',
      'Campaign ID': reply.campaign_id || 'N/A',
      'Category': reply.label_name || 'N/A',
      'Workspace': reply.workspace_name || 'N/A',
      'Reply Count': reply.reply_count || 'N/A',
      'Subject': reply.subject || 'N/A',
      'Reply Content': reply.text || 'N/A'
    }))

    const csv = Papa.unparse(csvData)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', `total-replies-${new Date().toISOString().split('T')[0]}.csv`)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const fetchDetailRecords = async (startDate: string, endDate: string) => {
    const { workspaceName } = await resolveClient(clientFilter)
    const { startDate: correctStartDate, endDate: correctEndDate } = getDateRange()

    let query = supabase
      .from('masterinbox_replies')
      .select('*')
      .eq('reply_count', 1)

    if (workspaceName) {
      query = query.eq('workspace_name', workspaceName)
    }

    query = query
      .gte('created_at', correctStartDate.toISOString())
      .lte('created_at', correctEndDate.toISOString())
      .order('created_at', { ascending: false })
      .limit(100)

    const { data, error } = await query

    if (error) {
      console.error('Error fetching detailed records:', error)
      throw error
    }

    return data || []
  }

  // Function to save category changes to database
  const saveCategory = async () => {
    if (!selectedReply || !editedCategory.trim()) return
    
    setSavingCategory(true)
    try {
      // Determine the reply type based on the new category
      const newReplyType = categorizeReply(editedCategory.trim())

      const { error } = await supabase
        .from('masterinbox_replies')
        .update({
          label_name: editedCategory.trim(),
          reply_category_type: newReplyType
        })
        .eq('id', selectedReply.id)
      
      if (error) throw error
      
      // Invalidate query to refresh data
      queryClient.invalidateQueries({ queryKey: ['totalRepliesCount', clientFilter, timeFrame, customDateRange] })
      
      // Update selected reply
      setSelectedReply(prev => ({ 
        ...prev, 
        category: editedCategory.trim(),
        reply_type_computed: newReplyType
      }))
      
      // Exit edit mode
      setEditingCategory(false)
      
      toast({
        title: "Category updated",
        description: "The reply category has been successfully updated.",
      })
    } catch (error) {
      console.error('Error updating category:', error)
      toast({
        title: "Error",
        description: "Failed to update category. Please try again.",
        variant: "destructive",
      })
    } finally {
      setSavingCategory(false)
    }
  }

  // Handle starting edit mode
  const startEditingCategory = () => {
    setEditedCategory(selectedReply?.category || '')
    setEditingCategory(true)
  }

  // Handle canceling edit mode
  const cancelEditingCategory = () => {
    setEditingCategory(false)
    setEditedCategory('')
  }

  return (
    <div className="space-y-6">
      {/* Header with Back Button and Timeframe Selector */}
      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
        
        {onTimeFrameChange && onCustomDateRangeChange && (
          <TimeframeSelector
            timeFrame={timeFrame}
            customDateRange={customDateRange}
            onTimeFrameChange={onTimeFrameChange}
            onCustomDateRangeChange={onCustomDateRangeChange}
          />
        )}
      </div>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Replies</CardTitle>
            <Reply className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatNumber(totalRepliesData.length)}</div>
            <p className="text-xs text-muted-foreground">
              {totalRepliesData.filter(r => r.reply_type_computed === 'positive').length} positive replies
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg per Day</CardTitle>
            <MessageSquare className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{calculateAvgPerDay()}</div>
            <p className="text-xs text-muted-foreground">Replies per day in timeframe</p>
          </CardContent>
        </Card>
      </div>

      {/* Category Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Category Breakdown</CardTitle>
            <CardDescription>Distribution of replies by category</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getCategoryBreakdown}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="category" 
                  tick={{ fontSize: 12 }}
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value: number, name: string) => [
                    `${value} replies (${getCategoryBreakdown.find(item => item.count === value)?.percentage || 0}%)`, 
                    'Count'
                  ]}
                />
                <Bar dataKey="count" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Reply Types</CardTitle>
            <CardDescription>Positive, negative, and auto-reply breakdown</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart 
                data={getSimplifiedCategoryBreakdown}
                onClick={(data) => {
                  if (data && data.activeLabel) {
                    const clickedCategory = data.activeLabel.toLowerCase();
                    const targetCategory = clickedCategory === 'auto-reply' ? 'auto-reply' : clickedCategory;
                    // Set filter to clicked category (don't toggle off if already selected)
                    setCategoryFilter(targetCategory);
                  }
                }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip formatter={(value: number) => [`${value} replies`, 'Count']} />
                <Bar dataKey="value" style={{ cursor: 'pointer' }}>
                  {getSimplifiedCategoryBreakdown.map((entry, index) => {
                    const isSelected = categoryFilter !== 'all' && entry.name.toLowerCase() === categoryFilter;
                    return (
                      <Cell 
                        key={`cell-${index}`} 
                        fill={isSelected ? entry.fill : entry.fill} 
                        fillOpacity={isSelected ? 1.0 : 0.7}
                        stroke={isSelected ? '#000' : 'none'}
                        strokeWidth={isSelected ? 2 : 0}
                      />
                    )
                  })}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            {categoryFilter !== 'all' && (
              <div className="mt-4 flex items-center justify-between">
                <div className="text-sm text-muted-foreground">
                  Filtered by: <span className="font-medium capitalize">{categoryFilter.replace('-', ' ')}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCategoryFilter('all')}
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear Filter
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Raw Replies Data Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-semibold">Raw Replies Data</CardTitle>
              <CardDescription>
                From masterinbox_replies - {filteredReplies.length} of {totalRepliesData?.length || 0} records shown
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <DataTableColumnConfig
                columns={columnConfigs}
                onColumnToggle={handleColumnToggle}
                onColumnReorder={handleColumnReorder}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={exportToCSV}
                disabled={filteredReplies.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter Controls */}
          {showFilters && (
            <div className="mb-6 p-4 border rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium">Filters</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearAllFilters}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4 mr-2" />
                  Clear All
                </Button>
              </div>
               <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                 <div>
                   <label className="text-sm font-medium mb-2 block">Category</label>
                   <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                     <SelectTrigger>
                       <SelectValue placeholder="All categories" />
                     </SelectTrigger>
                     <SelectContent>
                       <SelectItem value="all">All categories</SelectItem>
                       <SelectItem value="positive">Positive</SelectItem>
                       <SelectItem value="negative">Negative</SelectItem>
                       <SelectItem value="ooo_bounced">OOO/Bounced</SelectItem>
                       <SelectItem value="ai_messup">AI Messup</SelectItem>
                     </SelectContent>
                   </Select>
                 </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Workspace</label>
                  <Select value={clientIdFilter} onValueChange={setClientIdFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All workspaces" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All workspaces</SelectItem>
                      {uniqueWorkspaces.map(ws => (
                        <SelectItem key={ws} value={ws.toString()}>
                          {ws}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">From Email</label>
                  <Input
                    placeholder="Filter by from email..."
                    value={fromEmailFilter}
                    onChange={(e) => setFromEmailFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">To Email</label>
                  <Input
                    placeholder="Filter by to email..."
                    value={toEmailFilter}
                    onChange={(e) => setToEmailFilter(e.target.value)}
                  />
                </div>
                <div>
                  <label className="text-sm font-medium mb-2 block">Campaign</label>
                  <Input
                    placeholder="Filter by campaign..."
                    value={campaignFilter}
                    onChange={(e) => setCampaignFilter(e.target.value)}
                  />
                </div>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
              <p>Loading replies...</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="overflow-x-auto max-h-[600px] border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columnConfigs
                        .filter(config => config.visible)
                        .sort((a, b) => a.order - b.order)
                        .map(config => (
                          <TableHead key={config.id}>{config.label}</TableHead>
                        ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paginatedReplies.map((reply, index) => (
                      <TableRow 
                        key={index} 
                        className="hover:bg-muted/50 cursor-pointer transition-colors"
                        onClick={() => handleRowClick(reply)}
                      >
                        {columnConfigs
                          .filter(config => config.visible)
                          .sort((a, b) => a.order - b.order)
                          .map(config => {
                            const getCellClassName = (columnId: string) => {
                              switch (columnId) {
                                case 'time_replied':
                                case 'from_email':
                                case 'to_email':
                                case 'client_id':
                                case 'reply_count':
                                  return 'font-mono text-xs'
                                case 'to_name':
                                  return 'text-xs'
                                case 'campaign_name':
                                  return 'text-xs max-w-32 truncate'
                                case 'reply_body':
                                  return 'text-xs max-w-48 truncate'
                                case 'subject':
                                case 'sent_message':
                                  return 'text-xs max-w-64 truncate'
                                case 'app_url':
                                  return 'text-xs'
                                default:
                                  return 'text-xs'
                              }
                            }

                            const renderCellContent = (reply: any, columnId: string) => {
                              switch (columnId) {
                                case 'lead':
                                  return (
                                    <div className="text-xs">
                                      <div className="font-mono">{reply.email || 'N/A'}</div>
                                      <div className="text-muted-foreground">{reply.to_name || 'Unknown'}</div>
                                    </div>
                                  )
                                case 'sender':
                                  return (
                                    <div className="text-xs">
                                      <div className="font-mono">{reply.to_address || 'N/A'}</div>
                                    </div>
                                  )
                                case 'campaign':
                                  return (
                                    <div className="text-xs">
                                      <div title={reply.campaign_name}>{reply.campaign_name || 'N/A'}</div>
                                      <div className="text-muted-foreground font-mono">
                                        {reply.campaign_id || (reply.data as any)?.campaign_id || 'N/A'}
                                      </div>
                                    </div>
                                  )
                                case 'category':
                                  return (
                                    <div className="text-xs">
                                      <Badge variant="outline" className={`text-xs ${
                                        reply.reply_category_type === 'positive' ? 'text-green-600 border-green-200' :
                                        reply.reply_category_type === 'negative' ? 'text-red-600 border-red-200' :
                                        reply.reply_category_type === 'ooo_bounced' ? 'text-purple-600 border-purple-200' :
                                        'text-gray-600 border-gray-200'
                                      }`}>
                                        {reply.label_name || 'N/A'}
                                      </Badge>
                                      <div className="text-muted-foreground mt-1">
                                        <Badge variant="outline" className={`text-xs ${
                                          reply.reply_category_type === 'positive' ? 'text-green-600 border-green-200' :
                                          reply.reply_category_type === 'negative' ? 'text-red-600 border-red-200' :
                                          reply.reply_category_type === 'ooo_bounced' ? 'text-purple-600 border-purple-200' :
                                          'text-gray-600 border-gray-200'
                                        }`}>
                                          {reply.reply_category_type || 'N/A'}
                                        </Badge>
                                      </div>
                                    </div>
                                  )
                                case 'reply_count':
                                  return (
                                    <div className="text-xs">
                                      <div>{reply.reply_count}</div>
                                      <div className="text-muted-foreground font-mono">
                                        {reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A'}
                                      </div>
                                    </div>
                                  )
                                case 'reply':
                                  return (
                                    <div className="text-xs max-w-48 truncate" title={reply.text || reply.reply_message}>
                                      {formatEmailContentShort(reply.text || reply.reply_message)}
                                    </div>
                                  )
                                case 'actions':
                                  return (
                                    <div className="text-xs text-muted-foreground">
                                      Click row to view
                                    </div>
                                  )
                                // Legacy columns for backward compatibility
                                case 'time_replied':
                                  return reply.created_at ? new Date(reply.created_at).toLocaleString() : 'N/A'
                                case 'from_email':
                                  return reply.to_address || 'N/A'
                                case 'to_email':
                                  return reply.email || 'N/A'
                                case 'to_name':
                                  return reply.to_name || 'Unknown'
                                case 'campaign_name':
                                  return (
                                    <div title={reply.campaign_name}>
                                      {reply.campaign_name || 'N/A'}
                                    </div>
                                  )
                                case 'response_category':
                                  return (
                                    <Badge variant="outline" className={`text-xs ${
                                      reply.reply_category_type === 'positive' ? 'text-green-600 border-green-200' :
                                      reply.reply_category_type === 'negative' ? 'text-red-600 border-red-200' :
                                      'text-gray-600 border-gray-200'
                                    }`}>
                                      {reply.label_name || 'N/A'}
                                    </Badge>
                                   )
                                 case 'reply_type':
                                   return (
                                     <Badge variant="outline" className={`text-xs ${
                                       reply.reply_category_type === 'positive' ? 'text-green-600 border-green-200' :
                                       reply.reply_category_type === 'negative' ? 'text-red-600 border-red-200' :
                                       reply.reply_category_type === 'ooo_bounced' ? 'text-purple-600 border-purple-200' :
                                       'text-gray-600 border-gray-200'
                                     }`}>
                                       {reply.reply_category_type || 'N/A'}
                                     </Badge>
                                   )
                                case 'client_id':
                                  return reply.workspace_name || 'N/A'
                                case 'reply_body':
                                  return (
                                    <div title={formatEmailContentShort(reply.text)}>
                                      {formatEmailContentShort(reply.text)}
                                    </div>
                                  )
                                case 'subject':
                                  return (
                                    <div title={reply.subject}>
                                      {reply.subject || 'N/A'}
                                    </div>
                                  )
                                case 'sent_message':
                                  return (
                                    <div title={reply.sent_message}>
                                      {reply.sent_message || 'N/A'}
                                    </div>
                                  )
                                case 'app_url':
                                  return reply.app_url ? (
                                    <a 
                                      href={reply.app_url} 
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:text-blue-800 underline"
                                    >
                                      View
                                    </a>
                                  ) : 'N/A'
                                default:
                                  return reply[columnId] || 'N/A'
                              }
                            }

                            return (
                              <TableCell key={config.id} className={getCellClassName(config.id)}>
                                {renderCellContent(reply, config.id)}
                              </TableCell>
                            )
                          })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {filteredReplies.length === 0 && !isLoading && (
                <div className="text-center py-8 text-muted-foreground">
                  {(totalRepliesData?.length || 0) === 0 ? 'No replies found for the selected filters' : 'No replies match the current filters'}
                </div>
              )}
              
              {/* Pagination Controls */}
              {filteredReplies.length > itemsPerPage && (
                <div className="flex items-center justify-between px-2 py-4">
                  <div className="text-sm text-muted-foreground">
                    Showing {startIndex + 1} to {Math.min(endIndex, filteredReplies.length)} of {filteredReplies.length} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Previous
                    </Button>
                    <div className="flex items-center space-x-1">
                      {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                        const pageNum = i + 1
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(pageNum)}
                            className="w-8 h-8 p-0"
                          >
                            {pageNum}
                          </Button>
                        )
                      })}
                      {totalPages > 5 && (
                        <>
                          <span className="text-sm text-muted-foreground">...</span>
                          <Button
                            variant={currentPage === totalPages ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(totalPages)}
                            className="w-8 h-8 p-0"
                          >
                            {totalPages}
                          </Button>
                        </>
                      )}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Reply Detail Modal */}
          <ReplyDetailModal
            isOpen={isModalOpen}
            onClose={handleCloseModal}
            reply={selectedReply}
          />
        </CardContent>
      </Card>

    </div>
  )
}